import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Button,
  Card,
  Chip,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { vehicleBreakDownTableHeaders } from 'src/utils/tableHeaders';
import {
  camelCaseString,
  getFormattedDate,
  getFuelPrice,
  getSelectedSiteFilterValue
} from 'src/utils/getInitials';
import { TABLE_ROWS_PER_PAGE } from 'src/utils/constants';
import { getVehicleBreakDownList } from 'src/redux/vehicleBreakDownSlice';
import EnhancedTableCell from 'src/common/EnhancedTableCell';
import BreakdownApprovalForm from './BreakdownApprovalForm';

const BreakdownList = ({
  refreshData, searchText, siteFilter, ...rest
}) => {
  const dispatch = useDispatch();
  const { vehicleBreakDownList, loading, totalCount } = useSelector((state) => state.vehicleBreakDown);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [openReviewModal, setReviewModal] = useState(false);
  const [selectedBreakdown, setSelectedBreakdown] = useState({});
  const selectedSite = getSelectedSiteFilterValue(siteFilter);

  useEffect(() => {
    setPage(0);
    dispatch(getVehicleBreakDownList({
      limit, pageNo: 1, params: { vehicle__registration_num: searchText, site: selectedSite }
    }));
  }, [dispatch, limit, refreshData, searchText, selectedSite]);

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setPage(0);
    setLimit(newLimit);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    dispatch(getVehicleBreakDownList({
      limit, pageNo: newPage + 1, params: { vehicle__registration_num: searchText, site: selectedSite }
    }));
  };

  const reviewBreakdown = (breakdown) => {
    setSelectedBreakdown(breakdown);
    setReviewModal(true);
  };

  const handleRequestSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const getChipColor = (status) => {
    let chipColor = 'primary';
    if (status === 'completed') {
      chipColor = 'success';
    } else if (status === 'rejected') {
      chipColor = 'error';
    }
    return chipColor;
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Card {...rest}>
          <BreakdownApprovalForm
            open={openReviewModal}
            initialValues={selectedBreakdown}
            handleClose={() => setReviewModal(false)}
          />
          <PerfectScrollbar>
            <Box sx={{ minWidth: { xs: 760, sm: 900, md: 1050 } }}>
              <Table>
                <EnhancedTableHead
                  order={order}
                  orderBy={orderBy}
                  headCells={vehicleBreakDownTableHeaders}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {vehicleBreakDownList
                    .slice()
                    .sort(getComparator(order, orderBy))
                    // .slice(page * limit, page * limit + limit)
                    .map((vehicle) => (
                      <TableRow hover key={vehicle.id}>
                        <TableCell>
                          <Box sx={{ alignItems: 'center', display: 'flex' }}>
                            <Typography color="textPrimary" variant="body1">
                              {vehicle.id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{vehicle.vehicle_number}</TableCell>
                        <TableCell>{vehicle.type}</TableCell>
                        <TableCell>{vehicle.location}</TableCell>
                        <TableCell>{getFormattedDate(vehicle.start_time)}</TableCell>
                        <TableCell>{getFuelPrice(vehicle.expense)}</TableCell>
                        <TableCell>{vehicle.duration}</TableCell>
                        <EnhancedTableCell title={vehicle.description} />
                        <EnhancedTableCell title={vehicle.remarks} />
                        <TableCell>
                          <Chip
                            label={camelCaseString(vehicle.status)}
                            color={getChipColor(vehicle.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            onClick={() => reviewBreakdown(vehicle)}
                            disabled={vehicle.status !== 'pending'}
                            size="small"
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </PerfectScrollbar>
          <TablePagination
            component="div"
            showFirstButton
            showLastButton
            count={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={TABLE_ROWS_PER_PAGE}
          />
        </Card>
      )}
    </>
  );
};

BreakdownList.propTypes = {
  refreshData: PropTypes.bool,
  searchText: PropTypes.string,
  siteFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default BreakdownList;
